/**
 * Spacing Control Group Component
 *
 * A simplified spacing control group using WordPress BoxControl component
 * for padding and margin controls.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import { PanelBody, ResetButton } from '@redfox-thank-you/components';
import type { BoxValues } from '@redfox-thank-you/controls';
import {
	BaseControl,
	BoxControl,
	DEFAULT_UNITS,
} from '@redfox-thank-you/controls';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

// Import responsive utility to inherit values
import { deviceTypeToBreakpoint,getResponsiveValues } from '../common';
import type {
	BoxSpacingValues,
	SpacingControlProps,
	SpacingValues,
} from './types';

export const SpacingControl: React.FC<SpacingControlProps> = ({
	attributes,
	setAttributes,
	title = __('Spacing Settings', 'redfox-thank-you'),
	className = '',
	allowReset = true,
	initialOpen = false,
	enabledControls = {
		padding: true,
		margin: true,
		responsive: true,
	},
	showIndividualResets = true,
	showGroupReset = true,
}) => {
	// Get current device type for responsive controls
	const deviceType = useSelect((select) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, []);

	const activeBreakpoint = deviceTypeToBreakpoint(deviceType);

	/**
	 * Get spacing values for current breakpoint
	 */
	const getSpacingForBreakpoint = (spacingType: 'margin' | 'padding') => {
		// Get responsive spacing values with inheritance
		const breakpointData = getResponsiveValues<SpacingValues>(attributes, activeBreakpoint);
		const spacingData = breakpointData?.[spacingType];

		// Ensure BoxControl compatible values (without link property)
		return {
			top: spacingData?.top ?? '',
			right: spacingData?.right ?? '',
			bottom: spacingData?.bottom ?? '',
			left: spacingData?.left ?? '',
		};
	};

	/**
	 * Update spacing values for current breakpoint
	 */
	const updateSpacingForBreakpoint = (
		spacingType: 'margin' | 'padding',
		values: BoxValues | undefined,
	) => {
		const currentBreakpointData = attributes[activeBreakpoint] ?? {};
		const currentSpacingData = currentBreakpointData[spacingType];

		// Preserve the link property when updating values
		const updatedSpacingData = values
			? {
				...currentSpacingData,
				...values,
			}
			: undefined;

		const updatedBreakpointData = {
			...currentBreakpointData,
			[spacingType]: updatedSpacingData,
		};

		setAttributes({
			[activeBreakpoint]: updatedBreakpointData,
		});
	};

	/**
	 * Reset all spacing values
	 */
	const resetAllSpacing = () => {
		const resetValues = {
			top: '0px',
			right: '0px',
			bottom: '0px',
			left: '0px',
		};

		setAttributes({
			desktop: {
				margin: resetValues,
				padding: resetValues,
			},
			tablet: {
				margin: resetValues,
				padding: resetValues,
			},
			mobile: {
				margin: resetValues,
				padding: resetValues,
			},
		});
	};

	/**
	 * Reset individual spacing property
	 */
	const resetIndividualSpacing =
		(spacingType: 'margin' | 'padding') => () => {
			const resetValues = {
				top: '0px',
				right: '0px',
				bottom: '0px',
				left: '0px',
			};
			updateSpacingForBreakpoint(spacingType, resetValues);
		};

	/**
	 * Check if any spacing values are set
	 */
	const hasSpacingValues = (): boolean => {
		const checkValues = (
			breakpointData:
				| { padding?: BoxSpacingValues; margin?: BoxSpacingValues }
				| undefined,
		) => {
			if (!breakpointData) return false;
			const { padding, margin } = breakpointData;

			const hasNonZeroValues = (
				spacing: BoxSpacingValues | undefined,
			) => {
				if (!spacing) return false;
				return Object.values(spacing).some(
					(value) => value && value !== '0px',
				);
			};

			return hasNonZeroValues(padding) || hasNonZeroValues(margin);
		};

		return (
			checkValues(attributes.desktop) ||
			checkValues(attributes.tablet) ||
			checkValues(attributes.mobile)
		);
	};

	/**
	 * Check if individual spacing property has values
	 */
	const hasSpacingProperty = (
		spacingType: 'margin' | 'padding',
	): boolean => {
		const breakpointData = getResponsiveValues<SpacingValues>(attributes, activeBreakpoint);
		const spacingData = breakpointData?.[spacingType];

		if (!spacingData) return false;

		return Object.values(spacingData).some(
			(value) => value && value !== '0px' && value !== '0',
		);
	};

	/**
	 * Render header actions (reset button)
	 */
	const renderHeaderActions = () => {
		return (
			<ResetButton
				onReset={resetAllSpacing}
				show={allowReset && showGroupReset && hasSpacingValues()}
				label={__('Reset Spacing', 'redfox-thank-you')}
				className="redfox-thank-you-spacing-control__reset"
			/>
		);
	};

	return (
		<PanelBody
			title={title}
			initialOpen={initialOpen}
			className={`redfox-thank-you-spacing-control ${className}`}
			showResponsive={enabledControls?.responsive !== false}
			responsiveProps={{
				showLabels: false,
			}}
			headerActions={renderHeaderActions()}
		>
			{ /* Margin Control */}
			{enabledControls?.margin !== false && (
				<BaseControl
					label={__('Margin', 'redfox-thank-you')}
					className="redfox-thank-you-spacing-control__margin"
					showResetButton={showIndividualResets}
					onReset={resetIndividualSpacing('margin')}
					hasValue={hasSpacingProperty('margin')}
				>
					<BoxControl
						value={getSpacingForBreakpoint('margin')}
						onChange={(value) =>
							updateSpacingForBreakpoint('margin', value)
						}
						units={DEFAULT_UNITS}
						placeholder="0"
						min={0}
						resetFallbackValue="0px"
					/>
				</BaseControl>
			)}

			{ /* Padding Control */}
			{enabledControls?.padding !== false && (
				<BaseControl
					label={__('Padding', 'redfox-thank-you')}
					className="redfox-thank-you-spacing-control__padding"
					showResetButton={showIndividualResets}
					onReset={resetIndividualSpacing('padding')}
					hasValue={hasSpacingProperty('padding')}
				>
					<BoxControl
						value={getSpacingForBreakpoint('padding')}
						onChange={(value) =>
							updateSpacingForBreakpoint('padding', value)
						}
						units={DEFAULT_UNITS}
						placeholder="0"
						min={0}
						resetFallbackValue="0px"
					/>
				</BaseControl>
			)}
		</PanelBody>
	);
};
